import { useState, useMemo, useEffect } from 'react'
import Header from './components/Header'
import { useLocale } from './locales/useLocale'
import InputSection from './components/InputSection'
import ResultsDashboard from './components/ResultsDashboard'
import LoadingSpinner from './components/LoadingSpinner'
import { setSEOMetadata, generateHomeMetadata } from './utils/seoManager'
import { fetchWithRetry, API_BASE_URL } from './utils/api'

function App() {
  const { strings, locale } = useLocale()
  const { hero, footer } = strings
  const [analysisResults, setAnalysisResults] = useState(null)
  const [recommendations, setRecommendations] = useState([])
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false)
  const [analysisHistory, setAnalysisHistory] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // session and API base
  const sessionId = useMemo(() => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`), [])
  const apiUrl = API_BASE_URL
  const [feedbackContext, setFeedbackContext] = useState(null)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const metadata = generateHomeMetadata(locale)
    setSEOMetadata(metadata, locale)
  }, [locale])

  // string sha256 helper (Web Crypto)
  const sha256Hex = async (text) => {
    const enc = new TextEncoder()
    const data = enc.encode(text)
    const hash = await crypto.subtle.digest('SHA-256', data)
    const bytes = Array.from(new Uint8Array(hash))
    return bytes.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  const handleAnalyze = async (contentPayload, targetKeywords, meta = {}) => {
    setIsLoading(true)
    setError(null)
    setAnalysisResults(null)
    setRecommendations([])
    setIsLoadingRecommendations(false)

    try {
      const plainText = typeof contentPayload?.plain === 'string' ? contentPayload.plain : ''
      const htmlText = typeof contentPayload?.html === 'string' ? contentPayload.html : ''
      const markdownText = typeof contentPayload?.markdown === 'string' ? contentPayload.markdown : ''
      const email = typeof contentPayload?.email === 'string' ? contentPayload.email : ''
      const sourceUrl = typeof contentPayload?.contentUrl === 'string' ? contentPayload.contentUrl : null
      const finalUrl = typeof contentPayload?.fetchedUrl === 'string' ? contentPayload.fetchedUrl : null
      const fetchMeta = contentPayload?.fetchMeta || null
      const locale = meta?.locale || 'zh-TW'
      const formatHint = markdownText
        ? 'markdown'
        : htmlText
          ? 'html'
          : 'plain'

      const contentHash = await sha256Hex(plainText)
      setFeedbackContext({ sessionId, contentHash, targetKeywords, format: formatHint, sourceUrl: finalUrl || sourceUrl || null })

      // 如果有 email，使用異步分析流程
      if (email.trim()) {
        const requestPayload = meta?.inputMode === 'url' && sourceUrl
          ? {
              contentUrl: sourceUrl,
              fetchedUrl: finalUrl,
              fetchMeta,
              targetKeywords,
              email: email.trim(),
              locale,
              returnChunks: false,
              sessionId,
              mode: meta.mode,
              inputMode: meta.inputMode || meta.mode || 'manual',
              includeRecommendations: false
            }
          : {
              content: plainText,
              contentPlain: plainText,
              contentHtml: htmlText || null,
              contentMarkdown: markdownText || null,
              contentFormatHint: formatHint,
              contentUrl: sourceUrl,
              fetchedUrl: finalUrl,
              fetchMeta,
              targetKeywords,
              email: email.trim(),
              locale,
              returnChunks: false,
              sessionId,
              mode: meta.mode,
              inputMode: meta.inputMode || meta.mode || 'manual',
              includeRecommendations: false,
            }
        const asyncResponse = await fetchWithRetry(
          `${apiUrl}/api/analyze`,
          {
            method: 'POST',
            body: JSON.stringify(requestPayload),
          },
          2,
          1000
        )

        const asyncData = await asyncResponse.json()
        setAnalysisResults({
          status: asyncData.status || 'queued',
          taskId: asyncData.taskId,
          ownerToken: asyncData.ownerToken || null,
          resultUrl: asyncData.resultUrl || null,
          email: email.trim(),
          message: asyncData.message || `分析任務已提交，結果將寄送至 ${email.trim()}`
        })
        setIsLoading(false)
        return
      }

      // 同步分析流程
      const requestPayload = meta?.inputMode === 'url' && sourceUrl
        ? {
            contentUrl: sourceUrl,
            fetchedUrl: finalUrl,
            fetchMeta,
            targetKeywords,
            locale,
            returnChunks: true,
            sessionId,
            mode: meta.mode,
            inputMode: meta.inputMode || meta.mode || 'manual',
            includeRecommendations: false,
          }
        : {
            content: plainText,
            contentPlain: plainText,
            contentHtml: htmlText || null,
            contentMarkdown: markdownText || null,
            contentFormatHint: formatHint,
            contentUrl: sourceUrl,
            fetchedUrl: finalUrl,
            fetchMeta,
            targetKeywords,
            locale,
            returnChunks: true,
            sessionId,
            mode: meta.mode,
            inputMode: meta.inputMode || meta.mode || 'manual',
            includeRecommendations: false,
          }
      const response = await fetchWithRetry(
        `${apiUrl}/api/analyze`,
        {
          method: 'POST',
          body: JSON.stringify(requestPayload),
        },
        2,
        1000
      );

      // 如果代碼執行到這裡，表示請求成功

      const data = await response.json()

      if (data?.status === 'insufficient_metadata') {
        setAnalysisResults({
          status: 'insufficient_metadata',
          message: data?.message || '缺少 HTML metadata，請提供原始頁面再檢測。',
          contentSignals: data?.contentSignals || null,
          recommendationsStatus: 'unavailable'
        })
        setRecommendations([])
        return
      }

      const initialResults = {
        ...data,
        rawInput: {
          content: plainText,
          contentPlain: plainText,
          contentHtml: htmlText || null,
          contentMarkdown: markdownText || null,
          contentFormatHint: formatHint,
          contentUrl: sourceUrl,
          fetchedUrl: finalUrl,
          fetchMeta
        },
        targetKeywords,
        recommendationsStatus: data?.recommendationsStatus || 'not_requested'
      }
      setAnalysisResults(initialResults)
      setRecommendations(Array.isArray(data?.recommendations) ? data.recommendations : [])
      const timestamp = new Date().toISOString()
      setAnalysisHistory((prev) => {
        const next = [
          {
            id: `${timestamp}-${Math.random().toString(36).slice(2, 8)}`,
            timestamp,
            targetKeywords,
            pageReadinessScore: data?.pageReadinessScore ?? null,
            eligibilityScore: data?.eligibilityScore ?? null,
            extractabilityScore: data?.extractabilityScore ?? null,
            adoptionScore: data?.adoptionScore ?? null,
            inputMode: data?.inputMode || meta.inputMode || meta.mode || 'manual',
            sourceUrl: data?.sourceUrl || sourceUrl || null
          },
          ...prev
        ]
        return next.slice(0, 200)
      })
    } catch (err) {
      setError(err.message)
      console.error('Analysis error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateRecommendations = async () => {
    if (!analysisResults || analysisResults?.status === 'insufficient_metadata') return
    setIsLoadingRecommendations(true)
    setError(null)
    setRecommendations([])
    setAnalysisResults((prev) => (prev ? { ...prev, recommendationsStatus: 'loading' } : prev))
    try {
      const {
        rawInput = {},
        targetKeywords = [],
        scoreGuards,
        contentSignals,
        chunks
      } = analysisResults || {}
      // 過濾掉不可序列化的物件（如函數）
      const serializableScoreGuards = scoreGuards ? JSON.parse(JSON.stringify(scoreGuards, (key, value) => {
        return typeof value === 'function' ? undefined : value
      })) : undefined

      const serializableContentSignals = contentSignals ? JSON.parse(JSON.stringify(contentSignals, (key, value) => {
        return typeof value === 'function' ? undefined : value
      })) : undefined

      const response = await fetchWithRetry(
        `${apiUrl}/api/analyze`,
        {
          method: 'POST',
          body: JSON.stringify({
            content: rawInput?.content ?? null,
            contentPlain: rawInput?.contentPlain ?? null,
            contentHtml: rawInput?.contentHtml ?? null,
            contentMarkdown: rawInput?.contentMarkdown ?? null,
            contentFormatHint: rawInput?.contentFormatHint ?? null,
            contentUrl: rawInput?.contentUrl ?? null,
            fetchedUrl: rawInput?.fetchedUrl ?? null,
            fetchMeta: rawInput?.fetchMeta ?? null,
            targetKeywords,
            sessionId,
            includeRecommendations: true,
            returnChunks: Boolean(chunks?.length),
            scoreGuards: serializableScoreGuards,
            contentSignals: serializableContentSignals,
            inputMode: analysisResults?.inputMode || 'manual'
          })
        },
        2,
        1000
      )

      const data = await response.json()
      setAnalysisResults((prev) => ({
        ...prev,
        ...data,
        recommendationsStatus: data?.recommendationsStatus || 'ready'
      }))
      setRecommendations(Array.isArray(data?.recommendations) ? data.recommendations : [])
    } catch (err) {
      setError(err.message)
      console.error('Generate recommendations error:', err)
      setAnalysisResults((prev) => (prev ? { ...prev, recommendationsStatus: 'failed' } : prev))
    } finally {
      setIsLoadingRecommendations(false)
    }
  }

  const handleExportHistory = () => {
    if (!analysisHistory.length) return
    const headers = ['timestamp', 'keywords', 'pageReadiness', 'eligibility', 'extractability', 'adoption', 'inputMode', 'sourceUrl']
    const rows = analysisHistory.map((item) => {
      const keywords = Array.isArray(item.targetKeywords) ? item.targetKeywords.join('|') : ''
      return [
        item.timestamp,
        keywords,
        item.pageReadinessScore ?? '',
        item.eligibilityScore ?? '',
        item.extractabilityScore ?? '',
        item.adoptionScore ?? '',
        item.inputMode ?? '',
        item.sourceUrl ?? ''
      ]
    })
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => {
        if (cell === null || cell === undefined) return ''
        const value = String(cell)
        if (value.includes(',') || value.includes('\"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `analysis-history-${Date.now()}.csv`)
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleClearHistory = () => {
    if (!window.confirm('確定要清除所有分析歷史紀錄嗎？')) return
    setAnalysisHistory([])
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <section className="mb-8">
          <div className="bg-white border border-primary-100/60 rounded-2xl shadow-sm p-6 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: hero.intro }} />
            <p className="text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: hero.disclaimer }} />
          </div>
        </section>

        <InputSection onAnalyze={handleAnalyze} isLoading={isLoading} apiBaseUrl={apiUrl} sessionId={sessionId} />

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <p className="font-semibold">錯誤</p>
            <p>{error}</p>
          </div>
        )}

        {isLoading && (
          <div className="mt-8">
            <LoadingSpinner />
          </div>
        )}

        {analysisResults && !isLoading && (
          <div className="mt-8">
            <ResultsDashboard
              results={analysisResults}
              recommendations={recommendations}
              onGenerateRecommendations={handleGenerateRecommendations}
              generatingRecommendations={isLoadingRecommendations}
              feedbackContext={feedbackContext}
              apiBaseUrl={apiUrl}
            />
          </div>
        )}
      </main>

      <footer className="mt-16 py-8 bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4 text-center space-y-2">
          <p dangerouslySetInnerHTML={{ __html: footer.copy }} />
        </div>
      </footer>
    </div>
  )
}

export default App
